<template>
	<div class="site-root" :style="rootStyle" :class="{ 'site-root--fixed-bg': hasFixedBackground }">
		<div class="site-page" :style="pageStyle">
			<!-- Loading state -->
			<div v-if="loading" class="site-loading">
				<i class="fa fa-spinner fa-spin fa-2x"></i>
			</div>

			<!-- Password-protected page state -->
			<div v-else-if="passwordState.required" class="site-password-gate">
				<div class="site-password-gate__box">
					<i class="fa fa-lock site-password-gate__icon"></i>
					<h2 class="site-password-gate__title">{{ passwordState.pageTitle || 'Protected Page' }}</h2>
					<p class="site-password-gate__desc">This page is password protected. Please enter the password to view it.</p>
					<form class="site-password-gate__form" @submit.prevent="submitPassword">
						<input
							v-model="passwordState.input"
							type="password"
							class="site-password-gate__input"
							placeholder="Enter password…"
							:disabled="passwordState.submitting"
							ref="passwordInput"
						/>
						<button
							type="submit"
							class="site-password-gate__btn"
							:disabled="passwordState.submitting"
						>
							<i v-if="passwordState.submitting" class="fa fa-spinner fa-spin"></i>
							<span v-else>Unlock</span>
						</button>
					</form>
					<p v-if="passwordState.error" class="site-password-gate__error">
						<i class="fa fa-exclamation-circle"></i> {{ passwordState.error }}
					</p>
				</div>
			</div>

			<!-- Load-error state (network / server failure — not a 404) -->
			<div v-else-if="loadError" class="site-404">
				<h1>Something went wrong</h1>
				<p>We couldn't load this page. Please check your connection and try again.</p>
				<a href="#" class="site-404__link" @click.prevent="fetchPage(currentPath)">↻ Retry</a>
			</div>

			<!-- 404 state -->
			<div v-else-if="!pageData" class="site-404">
				<h1>Page Not Found</h1>
				<p>The page <strong>{{ currentPath }}</strong> does not exist.</p>
				<a href="/" class="site-404__link">← Back to Home</a>
			</div>

			<!-- Render page with shared header/footer -->
			<template v-else>
				<!-- Shared site header — each block gets a full-width section wrapper -->
				<header
					v-if="visibleHeaderBlocks.length"
					ref="siteHeader"
					class="site-header"
					:class="{ 'site-header--sticky': stickyHeaderMode === 'full' }"
				>
					<div
						v-for="block in visibleHeaderBlocks"
						:key="'hdr-' + block.id"
						class="site-section-outer"
						:style="sectionOuterStyle(block)"
					>
						<div class="site-section-inner" :style="getSectionInnerStyle(block)">
							<site-block-renderer :block="block" :band="isBand(block)" />
						</div>
					</div>
				</header>

				<!-- Thin bar with logo + menu once the full header has scrolled away -->
				<condensed-header-bar
					v-if="visibleHeaderBlocks.length && stickyHeaderMode === 'condensed' && headerEl"
					:header-blocks="visibleHeaderBlocks"
					:floating-blocks="visibleFloatingHeaderBlocks"
					:site-settings="siteSettings"
					:watch-el="headerEl"
				>
					<template slot="blocks" slot-scope="{ blocks }">
						<div v-for="block in blocks" :key="'flt-' + block.id" class="site-section-outer" :style="sectionOuterStyle(block)">
							<div class="site-section-inner" :style="getSectionInnerStyle(block)">
								<site-block-renderer :block="block" :band="isBand(block)" />
							</div>
						</div>
					</template>
					<template slot="nav" slot-scope="{ block }">
						<site-block-renderer :block="block" />
					</template>
				</condensed-header-bar>

				<!-- Page body blocks — each block gets a full-width section wrapper -->
				<main class="site-main">
					<div
						v-for="block in visibleBlocks"
						:key="block.id"
						class="site-section-outer"
						:style="sectionOuterStyle(block)"
					>
						<div class="site-section-inner" :style="getSectionInnerStyle(block)">
							<site-block-renderer :block="block" :band="isBand(block)" />
						</div>
					</div>
				</main>

				<!-- Shared site footer — each block gets a full-width section wrapper -->
				<footer v-if="visibleFooterBlocks.length" class="site-footer">
					<div
						v-for="block in visibleFooterBlocks"
						:key="'ftr-' + block.id"
						class="site-section-outer"
						:style="sectionOuterStyle(block)"
					>
						<div class="site-section-inner" :style="getSectionInnerStyle(block)">
							<site-block-renderer :block="block" :band="isBand(block)" />
						</div>
					</div>
				</footer>
			</template>
		</div>

		<!-- Accessibility widget — hidden inside the CMS preview iframe -->
		<accessibility-widget v-if="!isCmsPreview"></accessibility-widget>
	</div>
</template>

<script>
/**
 * Website SPA — App.vue
 * ─────────────────────
 * The root component for the public-facing Website renderer.
 *
 * On mount (and on popstate navigation) it reads the browser URL path
 * and fires an AJAX request to /ajax/getPage.php to fetch the matching
 * page from the CMS database. The domain config (Website/domains/)
 * tells the PHP endpoint which account database to connect to.
 */
import SiteBlockRenderer from './SiteBlockRenderer.vue';
import { buildSiteCssVars, stickyHeaderMode } from '@/websites/cms/blocks/shared/siteCssVars.js';
import CondensedHeaderBar from '@/websites/cms/blocks/shared/condensedHeaderBar.vue';
import { ensureSiteFonts } from '@/websites/cms/blocks/shared/siteFonts.js';
import { trackPageView } from './siteAnalytics.js';
import { isBlockVisible, isMobileViewport } from './viewport.js';
import { resolveBlockStyle, isBandBlock, isStretchedBand, splitBandStyle } from '@/websites/cms/blocks/editorCore/traits.js';
import { cssBackgroundImage } from '@/websites/cms/blocks/shared/blockUtils';

export default {
	name: 'WebsiteApp',

	components: {
		SiteBlockRenderer: SiteBlockRenderer,
		CondensedHeaderBar: CondensedHeaderBar
	},

	provide: function () {
		var self = this;
		// Stable references — Vue 2 inject is not reactive on reassignment,
		// but mutating properties on these objects propagates to all consumers.
		return {
			sitePageWidth: self.sharedSiteSettings.pageWidth || '1000px',
			siteSettings: self.sharedSiteSettings,
			siteNavigations: self.sharedNavigations,
			// current page identity for blocks that depend on it (Related posts, Author Box, Post Navigation)
			cmsPageContext: self.sharedPageContext
		};
	},

	data: function () {
		return {
			headerEl: null,
			sharedPageContext: { websitePostID: null, websitePageID: null, kind: 'page' },
			sharedSiteSettings: {},
			sharedNavigations: { bySlug: {}, byLocation: {} },
			headerBlocks: [],
			footerBlocks: [],
			// The thin header shown once the full header scrolls away (site-wide)
			floatingHeaderBlocks: [],
			pageData: null,
			currentPath: window.location.pathname || '/',
			loading: true,
			/** True when the last fetch failed at the network/server level (not a 404) */
			loadError: false,
			/** True when the page was loaded with ?preview=1 from the CMS editor */
			isCmsPreview: false,
			/** Signed preview token from the editor iframe URL (?ptoken=...) */
			cmsPreviewToken: null,
			/** Preview metadata from the most recent page load */
			currentPageIsDraft: false,
			currentPreviewMeta: null,
			/** Password-gate state for password-protected pages */
			passwordState: {
				required:   false,
				pageTitle:  '',
				websitePageID: null,
				input:      '',
				submitting: false,
				error:      ''
			}
		};
	},

	computed: {
		// Backwards-compatible alias used elsewhere in this component.
		siteSettings: function () { return this.sharedSiteSettings; },
		navigations:  function () { return this.sharedNavigations; },
		/**
		 * Filter blocks by visibility — reactive to viewport resizes.
		 */
		visibleBlocks: function () {
			if (!this.pageData || !Array.isArray(this.pageData.blocks)) return [];
			return this.pageData.blocks.filter(isBlockVisible);
		},

		/** Header/footer blocks filtered by the same visibility rules. */
		/** Per-page display options stored with the page content (hide the shared header / footer). */
		pageOptions: function () {
			return (this.pageData && this.pageData.pageOptions && typeof this.pageData.pageOptions === 'object') ? this.pageData.pageOptions : {};
		},

		visibleHeaderBlocks: function () {
			if (this.pageOptions.hideHeader) return [];
			return (this.headerBlocks || []).filter(isBlockVisible);
		},

		stickyHeaderMode: function () { return stickyHeaderMode(this.siteSettings && this.siteSettings.stickyHeader); },

		visibleFooterBlocks: function () {
			if (this.pageOptions.hideFooter) return [];
			return (this.footerBlocks || []).filter(isBlockVisible);
		},

		/** Floating header follows the full header: hidden with it on pages that hide the header. */
		visibleFloatingHeaderBlocks: function () {
			if (this.pageOptions.hideHeader) return [];
			return (this.floatingHeaderBlocks || []).filter(isBlockVisible);
		},

		rootStyle: function () {
			var s = this.siteSettings;
			var pm = (this.pageData && this.pageData.pageMeta) || {};

			// Background: page-level overrides site-level when set
			var bgColor = pm.pageBackgroundColor || s.pageBackgroundColor || '#FFFFFF';

			var style = Object.assign({
				backgroundColor: bgColor,
				// Body padding — pushes .site-page away from the viewport edges
				paddingTop:    s.bodyPaddingTop    || '0',
				paddingRight:  s.bodyPaddingRight  || '0',
				paddingBottom: s.bodyPaddingBottom || '0',
				paddingLeft:   s.bodyPaddingLeft   || '0',
				minHeight: '100vh'
			}, buildSiteCssVars(s));

			// Page-level background image (overrides site when set)
			if (pm.pageBackgroundImage) {
				if (pm.pageBackgroundAttachment === 'fixed') {
					// For fixed backgrounds, use CSS variables + ::before pseudo-element
					// to avoid the well-known browser issue where background-size:cover
					// stretches to the full element height instead of viewport height.
					// The root backgroundColor must be transparent so the ::before
					// pseudo-element (z-index: -1) is visible behind the content.
					style.backgroundColor = 'transparent';
					style['--page-bg-image'] = cssBackgroundImage(pm.pageBackgroundImage);
					style['--page-bg-size'] = pm.pageBackgroundSize || 'cover';
					style['--page-bg-position'] = pm.pageBackgroundPosition || 'center center';
					style['--page-bg-repeat'] = pm.pageBackgroundRepeat || 'no-repeat';
				} else {
					style.backgroundImage = cssBackgroundImage(pm.pageBackgroundImage);
					style.backgroundSize = pm.pageBackgroundSize || 'cover';
					style.backgroundPosition = pm.pageBackgroundPosition || 'center center';
					style.backgroundRepeat = pm.pageBackgroundRepeat || 'no-repeat';
				}
			}

			return style;
		},

		hasFixedBackground: function () {
			var pm = (this.pageData && this.pageData.pageMeta) || {};
			return !!(pm.pageBackgroundImage && pm.pageBackgroundAttachment === 'fixed');
		},

		pageStyle: function () {
			var style = {
				margin: '0 auto',
				overflow: 'clip'
			};
			return style;
		},

		/**
		 * Applied to the inner wrapper of each section to constrain content
		 * to the configured page width and centre it horizontally.
		 */
		sectionInnerStyle: function () {
			var style = {
				maxWidth: this.siteSettings.pageWidth || '1000px',
				margin: '0 auto',
				backgroundColor: this.siteSettings.pageColor || '#FFFFFF'
			};
			return style;
		}
	},

	mounted: function () {
		var self = this;

		// Detect CMS preview mode from URL query string (?preview=1)
		var params = new URLSearchParams(window.location.search);
		this.isCmsPreview = params.get('preview') === '1';
		this.cmsPreviewToken = params.get('ptoken') || null;

		// Fetch the initial page
		this.fetchPage(this.currentPath);

		// Listen for browser back/forward navigation
		window.addEventListener('popstate', function () {
			self.currentPath = window.location.pathname || '/';
			self.fetchPage(self.currentPath);
		});

		// Intercept internal link clicks for SPA navigation.
		// Leave modified clicks (new tab), downloads, external targets, and
		// in-page anchors to the browser.
		document.addEventListener('click', function (e) {
			if (e.defaultPrevented) return;
			if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
			var target = e.target.closest('a');
			if (!target) return;
			if (target.target && target.target !== '_self') return;
			if (target.hasAttribute('download')) return;
			var href = target.getAttribute('href');
			if (!href || href.charAt(0) !== '/') return;
			// Don't hijack direct file/asset/endpoint paths
			if (/^\/(media|dist|ajax)\//.test(href)) return;
			// In-page or cross-page anchors: let the browser handle scrolling
			if (href.indexOf('#') !== -1) return;

			e.preventDefault();
			if (href !== self.currentPath) {
				self.currentPath = href;
				window.history.pushState(null, '', href);
				self.fetchPage(href);
				window.scrollTo(0, 0);
			}
		});

		// Listen for navigation commands from the CMS editor parent frame.
		// Only accept messages from the editor's origin (derived from the
		// embedding frame) — never from arbitrary parents.
		if (this.isCmsPreview) {
			window.addEventListener('message', function (event) {
				if (!event || !event.data) return;
				var editorOrigin = self.getEditorOrigin();
				if (!editorOrigin || event.origin !== editorOrigin) return;
				var msg = event.data;
				if (msg.type !== 'cms-preview-command') return;

				if (msg.command === 'back') {
					window.history.back();
				} else if (msg.command === 'forward') {
					window.history.forward();
				} else if (msg.command === 'refresh') {
					self.fetchPage(self.currentPath);
				}
			});
		}
	},

	methods: {
		/**
		 * Resolves the origin of the embedding CMS editor frame.
		 * Uses location.ancestorOrigins where available (Chrome/Safari),
		 * falling back to document.referrer (set on initial iframe load).
		 * Returns null when the origin cannot be determined — callers must
		 * treat that as "do not communicate".
		 *
		 * @returns {string|null}
		 */
		getEditorOrigin: function () {
			try {
				if (window.location.ancestorOrigins && window.location.ancestorOrigins.length) {
					return window.location.ancestorOrigins[0];
				}
				if (document.referrer) {
					return new URL(document.referrer).origin;
				}
			} catch (e) { /* fall through */ }
			return null;
		},

		/**
		 * Returns the inner section style for a given block.
		 * If the block has sectionFullWidth enabled, the max-width constraint
		 * is removed so the content spans the full viewport width.
		 */
		getSectionInnerStyle: function (block) {
			if (this.isBand(block)) {
				// band: the outer section carries the background; content stays at page width unless stretched
				if (isStretchedBand(block)) return { width: '100%' };
				return { maxWidth: this.siteSettings.pageWidth || '1000px', margin: '0 auto' };
			}
			return this.sectionInnerStyle;
		},

		isBand: function (block) { return isBandBlock(block); },

		/**
		 * Computes the outer section wrapper style for a single block.
		 * The outer wrapper spans full canvas width and carries the section-level
		 * background (sectionBackgroundColor / sectionBackgroundImage) from the
		 * block's props.  The inner wrapper constrains content to pageWidth.
		 *
		 * @param {Object} block
		 * @returns {Object} CSS style object
		 */
		sectionOuterStyle: function (block) {
			var style = { width: '100%' };
			var props = (block && block.props) || {};
			if (this.isBand(block)) {
				var resolved = resolveBlockStyle(block.style, { isMobile: isMobileViewport(), siteSettings: this.siteSettings });
				Object.assign(style, splitBandStyle(resolved).outer);
			}

			if (props.sectionBackgroundColor) {
				style.backgroundColor = props.sectionBackgroundColor;
			}
			if (props.sectionBackgroundImage) {
				style.backgroundImage = cssBackgroundImage(props.sectionBackgroundImage);
				style.backgroundSize = props.sectionBackgroundSize || 'cover';
				style.backgroundPosition = props.sectionBackgroundPosition || 'center center';
				style.backgroundRepeat = 'no-repeat';
			}
			return style;
		},

		/**
		 * Fetch page data from the server via AJAX.
		 * Appends ?preview=1 when running inside the CMS editor iframe.
		 * Aborts any in-flight request so rapid navigation can't produce
		 * stale-response races.
		 * @param {string} slug — the URL path to look up
		 * @param {number} [redirectDepth] — internal redirect-loop guard
		 */
		fetchPage: function (slug, redirectDepth) {
			var self = this;
			redirectDepth = redirectDepth || 0;
			self.loading = true;
			self.loadError = false;
			// Clear any lingering password gate when navigating to a new page
			self.passwordState.required = false;
			self.passwordState.pageTitle = '';
			self.passwordState.websitePageID = null;
			self.passwordState.input = '';
			self.passwordState.error = '';

			var url = '/ajax/getPage.php?slug=' + encodeURIComponent(slug);
			if (self.isCmsPreview) {
				url += '&preview=1';
				if (self.cmsPreviewToken) {
					url += '&ptoken=' + encodeURIComponent(self.cmsPreviewToken);
				}
			}

			// Abort any in-flight fetch — its response is now stale
			if (self._activeXhr) {
				try { self._activeXhr.abort(); } catch (abortErr) { /* noop */ }
				self._activeXhr = null;
			}

			var xhr = new XMLHttpRequest();
			self._activeXhr = xhr;
			xhr.open('GET', url, true);
			xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
			xhr.onreadystatechange = function () {
				if (xhr.readyState !== 4) return;
				// Ignore aborted/superseded requests
				if (self._activeXhr !== xhr) return;
				self._activeXhr = null;

				self.loading = false;

				if (xhr.status === 200) {
					try {
						var data = JSON.parse(xhr.responseText);
						if (data.success) {

							// ── Check for a server-side redirect ──
							if (data.redirect && data.redirect.to) {
								var dest = data.redirect.to;
								// External URL (has a protocol): do a hard navigate
								if (/^https?:\/\//i.test(dest)) {
									window.location.replace(dest);
									return;
								}
								// Internal path: SPA navigate (bounded to avoid loops)
								if (redirectDepth >= 5) {
									self.pageData = null;
									return;
								}
								self.currentPath = dest;
								window.history.replaceState(null, '', dest);
								self.fetchPage(dest, redirectDepth + 1);
								return;
							}

							// ── Check for a password-protected page ──
							if (data.passwordRequired) {
								self.pageData = null;
								self.passwordState.required = true;
								self.passwordState.pageTitle = data.pageTitle || '';
								self.passwordState.websitePageID = data.websitePageID || null;
								self.passwordState.input = '';
								self.passwordState.error = '';
								self.$nextTick(function () {
									if (self.$refs.passwordInput) {
										self.$refs.passwordInput.focus();
									}
								});
								return;
							}

							var page = data.page || null;

							// Ensure blocks is always an array (may arrive as a JSON string
							// or as an object with nested keys from the CMS content)
							if (page && page.blocks) {
								if (!Array.isArray(page.blocks)) {
									// If blocks is an object with a nested "blocks" array,
									// extract the inner array and hoist siteSettings/header/footer
									if (page.blocks.blocks && Array.isArray(page.blocks.blocks)) {
										if (page.blocks.siteSettings && !data.siteSettings) {
											data.siteSettings = page.blocks.siteSettings;
										}
										if (page.blocks.headerBlocks && !data.headerBlocks) {
											data.headerBlocks = page.blocks.headerBlocks;
										}
										if (page.blocks.footerBlocks && !data.footerBlocks) {
											data.footerBlocks = page.blocks.footerBlocks;
										}
										if (page.blocks.floatingHeaderBlocks && !data.floatingHeaderBlocks) {
											data.floatingHeaderBlocks = page.blocks.floatingHeaderBlocks;
										}
										page.blocks = page.blocks.blocks;
									} else if (typeof page.blocks === 'string') {
										try {
											page.blocks = JSON.parse(page.blocks);
										} catch (parseErr) {
											page.blocks = [];
										}
									} else {
										page.blocks = [];
									}
								}
							}

							self.pageData = page;

							// Store preview metadata for the draft indicator
							if (self.isCmsPreview && data.previewMeta) {
								self.currentPreviewMeta = data.previewMeta;
								self.currentPageIsDraft = !!data.previewMeta.isDraft;
							} else {
								self.currentPreviewMeta = null;
								self.currentPageIsDraft = false;
							}

							// Update site-wide data on first successful load.
							// MUST mutate the existing objects (not reassign) so injected
							// references remain valid in child components.
							if (data.siteSettings) {
								self.replaceSharedObject(self.sharedSiteSettings, data.siteSettings);
							}
							if (data.headerBlocks) {
								self.headerBlocks = data.headerBlocks;
							}
							if (data.footerBlocks) {
								self.footerBlocks = data.footerBlocks;
							}
							if (data.floatingHeaderBlocks) {
								self.floatingHeaderBlocks = data.floatingHeaderBlocks;
							}
							if (data.navigations) {
								self.replaceSharedObject(self.sharedNavigations, data.navigations);
							}
							// story ids arrive as "wp-<websitePostID>"
							var pid = String(data.websitePageID || '');
							self.sharedPageContext.websitePostID = pid.indexOf('wp-') === 0 ? parseInt(pid.substring(3), 10) || null : null;
							self.sharedPageContext.websitePageID = data.websitePageID || null;
							self.sharedPageContext.kind = (data.page && data.page.seo && data.page.seo.kind) || 'page';

							self.updateTitle();

							// Google Analytics page view (never inside the editor preview).
							if (!self.isCmsPreview && data.tracking) {
								trackPageView(data.tracking, { path: slug, title: document.title });
							}

							// In CMS preview mode, notify the parent editor frame
							// so the design nav bar can update its browser-style status bar.
							if (self.isCmsPreview) {
								self.emitPreviewNavigation(data, page, slug);
							}
						} else {
							// success:false with a 200 = a real "not found" page
							self.pageData = null;
						}
					} catch (e) {
						self.pageData = null;
						self.loadError = true;
					}
				} else if (xhr.status === 404) {
					self.pageData = null;
				} else {
					// Network failure or server error — show retry, not 404
					self.pageData = null;
					self.loadError = true;
				}
			};
			xhr.send();
		},

		updateTitle: function () {
			if (this.pageData && this.pageData.title) {
				document.title = this.pageData.title;
			} else if (this.sharedSiteSettings && this.sharedSiteSettings.siteName) {
				document.title = this.sharedSiteSettings.siteName;
			}
		},

		/**
		 * Send a postMessage to the parent CMS editor frame with the current
		 * page's navigation state.  The design nav bar listens for this message
		 * and updates the browser-style URL bar + edit buttons.
		 *
		 * @param {Object} data   - Full server response
		 * @param {Object} page   - page sub-object
		 * @param {string} slug   - current URL path
		 */
		emitPreviewNavigation: function (data, page, slug) {
			var meta = data.previewMeta || {};
			var message = {
				type:            'cms-preview-navigation',
				pageTitle:       (page && page.title) || '',
				pageSlug:        slug || window.location.pathname,
				websitePageID:   data.websitePageID || null,
				websitePostID:   meta.websitePostID || null,
				templatePageID:  meta.templatePageID || null,
				isDraft:         !!(meta.isDraft),
				pageType:        meta.pageType || 'article',
				livePageID:      meta.livePageID || data.websitePageID || null
			};
			try {
				var editorOrigin = this.getEditorOrigin();
				if (editorOrigin && window.parent !== window) {
					window.parent.postMessage(message, editorOrigin);
				}
			} catch (e) {
				// Cross-origin or no parent frame — ignore
			}
		},

		/**
		 * Submit the password entered in the password gate form.
		 * POSTs to /ajax/submitPagePassword.php; on success re-fetches the page.
		 */
		submitPassword: function () {
			var self = this;
			if (!self.passwordState.input || self.passwordState.submitting) { return; }

			self.passwordState.submitting = true;
			self.passwordState.error = '';

			var formData = new FormData();
			formData.append('websitePageID', self.passwordState.websitePageID);
			formData.append('password', self.passwordState.input);

			var xhr = new XMLHttpRequest();
			xhr.open('POST', '/ajax/submitPagePassword.php', true);
			xhr.onreadystatechange = function () {
				if (xhr.readyState !== 4) return;
				self.passwordState.submitting = false;

				try {
					var data = JSON.parse(xhr.responseText);
					if (data.success) {
						// Password accepted — clear the gate and re-fetch the page
						self.passwordState.required = false;
						self.passwordState.input = '';
						self.fetchPage(self.currentPath);
					} else {
						self.passwordState.error = data.errorMessage || 'Incorrect password.';
					}
				} catch (e) {
					self.passwordState.error = 'An error occurred. Please try again.';
				}
			};
			xhr.send(formData);
		},

		/**
		 * Replace the contents of a shared/injected object in place so that
		 * descendants reading via `inject` continue to see the updated values.
		 * Vue 2's provide/inject is not reactive across reassignment, but
		 * mutations to the same object reference do propagate.
		 */
		replaceSharedObject: function (target, source) {
			if (!target || !source) { return; }
			var key;
			// Remove keys that are no longer present
			for (key in target) {
				if (Object.prototype.hasOwnProperty.call(target, key) && !(key in source)) {
					this.$delete(target, key);
				}
			}
			// Set / overwrite from source
			for (key in source) {
				if (Object.prototype.hasOwnProperty.call(source, key)) {
					this.$set(target, key, source[key]);
				}
			}
		},

		/**
		 * Injects or updates a <style> element containing the user's custom CSS.
		 */
		injectCustomCss: function (css) {
			var id = 'site-custom-css';
			var el = document.getElementById(id);
			if (!css) {
				if (el) el.remove();
				return;
			}
			if (!el) {
				el = document.createElement('style');
				el.id = id;
				el.type = 'text/css';
				document.head.appendChild(el);
			}
			el.textContent = css;
		}
	},

	watch: {
		/** Load the web fonts the site tokens name (Google Fonts). */
		siteSettings: {
			immediate: true,
			deep: true,
			handler: function (s) { ensureSiteFonts(s); }
		},
		pageData: function () {
			this.updateTitle();
		},
		'siteSettings.customCss': {
			immediate: true,
			handler: function (val) {
				this.injectCustomCss(val || '');
			}
		}
	},

	updated: function () {
		// $refs are not reactive: hand the header element to the thin bar once it exists
		var el = this.$refs.siteHeader || null;
		if (el !== this.headerEl) this.headerEl = el;
	},

	beforeDestroy: function () {
		var el = document.getElementById('site-custom-css');
		if (el) el.remove();
	}
};
</script>

<style>
/* Global reset for the Website SPA */
.site-root {
	font-family: var(--site-body-font, 'Open Sans', sans-serif);
	font-size: 16px;
	line-height: 1.6;
	color: var(--site-body-color, #333333);
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	box-sizing: border-box;
	position: relative;
}

/* Fixed background image — uses a pseudo-element pinned to the viewport
   so that background-size:cover covers only the visible area, not the
   full scrollable height of the page. */
.site-root--fixed-bg {
	position: relative;
}
.site-root--fixed-bg::before {
	content: '';
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: -1;
	background-image: var(--page-bg-image);
	background-size: var(--page-bg-size, cover);
	background-position: var(--page-bg-position, center center);
	background-repeat: var(--page-bg-repeat, no-repeat);
	pointer-events: none;
}

.site-root *,
.site-root *::before,
.site-root *::after {
	box-sizing: border-box;
}

/* Typography defaults driven by site settings */
.site-root h1,
.site-root h2,
.site-root h3,
.site-root h4,
.site-root h5,
.site-root h6 {
	font-family: var(--site-heading-font, 'Varela Round', sans-serif);
	color: var(--site-heading-color, #1E2031);
}

.site-root h1 {
	font-family: var(--site-h1-font, var(--site-heading-font, 'Varela Round', sans-serif));
	font-size: var(--site-h1-size, 2.5em);
	font-weight: var(--site-h1-weight, 700);
	font-style: var(--site-h1-style, normal);
	text-decoration: var(--site-h1-decoration, none);
	line-height: var(--site-h1-line-height, 1.2);
	letter-spacing: var(--site-h1-letter-spacing, 0);
	text-transform: var(--site-h1-transform, none);
	color: var(--site-h1-color, var(--site-heading-color, #1E2031));
}
.site-root h2 {
	font-family: var(--site-h2-font, var(--site-heading-font, 'Varela Round', sans-serif));
	font-size: var(--site-h2-size, 2em);
	font-weight: var(--site-h2-weight, 700);
	font-style: var(--site-h2-style, normal);
	text-decoration: var(--site-h2-decoration, none);
	line-height: var(--site-h2-line-height, 1.25);
	letter-spacing: var(--site-h2-letter-spacing, 0);
	text-transform: var(--site-h2-transform, none);
	color: var(--site-h2-color, var(--site-heading-color, #1E2031));
}
.site-root h3 {
	font-family: var(--site-h3-font, var(--site-heading-font, 'Varela Round', sans-serif));
	font-size: var(--site-h3-size, 1.75em);
	font-weight: var(--site-h3-weight, 600);
	font-style: var(--site-h3-style, normal);
	text-decoration: var(--site-h3-decoration, none);
	line-height: var(--site-h3-line-height, 1.3);
	letter-spacing: var(--site-h3-letter-spacing, 0);
	text-transform: var(--site-h3-transform, none);
	color: var(--site-h3-color, var(--site-heading-color, #1E2031));
}
.site-root h4 {
	font-family: var(--site-h4-font, var(--site-heading-font, 'Varela Round', sans-serif));
	font-size: var(--site-h4-size, 1.5em);
	font-weight: var(--site-h4-weight, 600);
	font-style: var(--site-h4-style, normal);
	text-decoration: var(--site-h4-decoration, none);
	line-height: var(--site-h4-line-height, 1.35);
	letter-spacing: var(--site-h4-letter-spacing, 0);
	text-transform: var(--site-h4-transform, none);
	color: var(--site-h4-color, var(--site-heading-color, #1E2031));
}
.site-root h5 {
	font-family: var(--site-h5-font, var(--site-heading-font, 'Varela Round', sans-serif));
	font-size: var(--site-h5-size, 1.25em);
	font-weight: var(--site-h5-weight, 600);
	font-style: var(--site-h5-style, normal);
	text-decoration: var(--site-h5-decoration, none);
	line-height: var(--site-h5-line-height, 1.4);
	letter-spacing: var(--site-h5-letter-spacing, 0);
	text-transform: var(--site-h5-transform, none);
	color: var(--site-h5-color, var(--site-heading-color, #1E2031));
}
.site-root h6 {
	font-family: var(--site-h6-font, var(--site-heading-font, 'Varela Round', sans-serif));
	font-size: var(--site-h6-size, 1em);
	font-weight: var(--site-h6-weight, 600);
	font-style: var(--site-h6-style, normal);
	text-decoration: var(--site-h6-decoration, none);
	line-height: var(--site-h6-line-height, 1.45);
	letter-spacing: var(--site-h6-letter-spacing, 0);
	text-transform: var(--site-h6-transform, none);
	color: var(--site-h6-color, var(--site-heading-color, #1E2031));
}

.site-root a {
	color: var(--site-link-color, #FB6A00);
}

/* Paragraph defaults driven by site settings */
.site-root p {
	margin: var(--site-p-margin, 0 0 1rem);
	line-height: var(--site-p-line-height, 1.6);
}

/* Blockquote defaults driven by site settings */
.site-root blockquote {
	font-size: var(--site-bq-font-size, 1.125em);
	font-style: var(--site-bq-font-style, italic);
	line-height: var(--site-bq-line-height, 1.6);
	color: var(--site-bq-color, #555555);
	border-left: var(--site-bq-border-width, 4px) solid var(--site-bq-border-color, #FB6A00);
	background-color: var(--site-bq-bg, #FAFAFA);
	padding: var(--site-bq-padding, 1rem 1.5rem);
	margin: var(--site-bq-margin, 1.5rem 0);
	border-radius: 0 4px 4px 0;
}

.site-root blockquote p {
	margin: 0;
}

/* Inline code defaults driven by site settings */
.site-root code {
	font-family: var(--site-code-font, 'Fira Code', monospace);
	font-size: var(--site-code-font-size, 0.875em);
	color: var(--site-code-color, #E31515);
	background-color: var(--site-code-bg, #F4F4F5);
	border-radius: var(--site-code-radius, 3px);
	padding: var(--site-code-padding, 0.15em 0.4em);
}

/* Code block (pre) defaults driven by site settings */
.site-root pre {
	font-family: var(--site-pre-font, 'Fira Code', monospace);
	font-size: var(--site-pre-font-size, 0.875em);
	color: var(--site-pre-color, #E8E8E8);
	background-color: var(--site-pre-bg, #1E2031);
	border-radius: var(--site-pre-radius, 6px);
	padding: var(--site-pre-padding, 1rem 1.25rem);
	line-height: var(--site-pre-line-height, 1.6);
	overflow-x: auto;
	margin: var(--site-bq-margin, 1.5rem 0);
	white-space: pre-wrap;
	word-wrap: break-word;
}

.site-root pre code {
	font-family: inherit;
	font-size: inherit;
	color: inherit;
	background: transparent;
	padding: 0;
	border-radius: 0;
}

/* Horizontal rule defaults driven by site settings */
.site-root hr {
	border: none;
	border-top: var(--site-hr-height, 1px) var(--site-hr-style, solid) var(--site-hr-color, #DDDEE0);
	margin: var(--site-hr-margin, 2rem 0);
}

/* List defaults driven by site settings */
.site-root ul,
.site-root ol {
	padding-left: var(--site-list-padding, 1.5rem);
	margin: 0 0 1rem;
}

.site-root li {
	margin-bottom: var(--site-list-spacing, 0.35rem);
}

.site-root ul li::marker {
	color: var(--site-list-marker-color, #FB6A00);
}

.site-root ol li::marker {
	color: var(--site-list-marker-color, #FB6A00);
}

.site-page {
	position: relative;
}

.site-loading {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 60vh;
	color: #999;
}

.site-404 {
	text-align: center;
	padding: 4rem 2rem;
	min-height: 60vh;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
}

.site-404 h1 {
	margin: 0 0 0.5rem;
	font-size: 2em;
	color: #1E2031;
	text-transform: none;
}

.site-404 p {
	margin: 0 0 1.5rem;
	color: #7f8c99;
}

.site-404__link {
	color: #FB6A00;
	text-decoration: none;
	font-weight: 600;
}

.site-404__link:hover {
	text-decoration: underline;
}

/* ── Header stacking ───────────────────────────────── */
/* The header paints above the page body so open menu dropdowns are never
   covered by later content that creates its own stacking context (images,
   overlays, embeds). */
.site-header {
	position: relative;
	z-index: 100;
}
.site-header--sticky {
	position: sticky;
	top: 0;
}

/* ── Section wrapper (full-width band model) ──────── */
/* Each root block is wrapped in an outer div that fills the full viewport
   width — allowing edge-to-edge section backgrounds — and an inner div
   that constrains content to the configured pageWidth. */
.site-section-outer {
	width: 100%;
}

.site-section-inner {
	width: 100%;
}

/* ── Password gate ─────────────────────────────────── */
.site-password-gate {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 60vh;
	padding: 2rem;
}

.site-password-gate__box {
	background: #FFFFFF;
	border: 1px solid #DDDEE0;
	border-radius: 8px;
	padding: 2.5rem 2rem;
	max-width: 400px;
	width: 100%;
	text-align: center;
	box-shadow: 0 2px 12px rgba(0,0,0,0.07);
}

.site-password-gate__icon {
	font-size: 2.5em;
	color: #1E2031;
	margin-bottom: 0.75rem;
}

.site-password-gate__title {
	font-size: 1.4em;
	font-weight: 700;
	color: #1E2031;
	margin: 0 0 0.5rem;
	text-transform: none;
}

.site-password-gate__desc {
	font-size: 0.95em;
	color: #7f8c99;
	margin: 0 0 1.5rem;
	line-height: 1.5;
}

.site-password-gate__form {
	display: flex;
	gap: 0.5rem;
}

.site-password-gate__input {
	flex: 1;
	padding: 0.55rem 0.75rem;
	border: 1px solid #DDDEE0;
	border-radius: 4px;
	font-size: 1em;
	color: #1E2031;
	outline: none;
	transition: border-color 0.15s;
}

.site-password-gate__input:focus {
	border-color: #FB6A00;
}

.site-password-gate__btn {
	padding: 0.55rem 1.25rem;
	background: #FB6A00;
	color: #FFFFFF;
	border: none;
	border-radius: 4px;
	font-size: 1em;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s;
	white-space: nowrap;
}

.site-password-gate__btn:hover:not(:disabled) {
	background: #e55f00;
}

.site-password-gate__btn:disabled {
	opacity: 0.65;
	cursor: not-allowed;
}

.site-password-gate__error {
	margin: 0.75rem 0 0;
	font-size: 0.9em;
	color: #E31515;
}
</style>
